import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Image } from "react-bootstrap";
import {
  FORUM_SETTINGS_GET,
  FORUM_SETTINGS_UPDATE,
} from "../../routes/routes";
import { FORUM_SETTINGS_UPDATED } from "../../constant/constant";
import { ValidationMessage } from "../../components/Utils";
import { FullPageLoader } from "../../components/common/Loader";
import { usePermission } from "../../hooks/usePermission";
import { useApplicationContext } from "../../context/ApplicationContext";

const FORUM_QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ["bold"],
    [{ list: "bullet" }],
  ],
};
const FORUM_QUILL_FORMATS = ["header", "bold", "list"];

const emptySmtp = {
  mail_from_name: "",
  mail_from_email: "",
  mail_to_email: "",
  smtp_host: "",
  smtp_port: "465",
  smtp_user: "",
  smtp_pass: "",
  smtp_secure: "1",
};

const ForumSettingsEdit = () => {
  const { hasPermission } = usePermission();
  const { refreshForumBranding } = useApplicationContext();
  const canEdit = hasPermission("forum-settings-edit");

  const [terms, setTerms] = useState({ title: "", content: "" });
  const [privacy, setPrivacy] = useState({ title: "", content: "" });
  const [smtp, setSmtp] = useState(emptySmtp);
  const [applicationName, setApplicationName] = useState("");

  const [logoPreview, setLogoPreview] = useState("");
  const [faviconPreview, setFaviconPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);

  const [savingSection, setSavingSection] = useState("");
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const onFaviconChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaviconFile(file);
    setFaviconPreview(URL.createObjectURL(file));
  };

  const handleApiError = (err) => {
    if (err.response?.status === 422) {
      setErrors(err.response.data.error || {});
      toast.error(err.response.data.msg);
    } else {
      toast.error(err.response?.data?.msg || "Something went wrong");
    }
  };

  const putSection = (formData) => {
    return axios.put(FORUM_SETTINGS_UPDATE, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const saveBranding = () => {
    if (!canEdit) return;
    const appName = String(applicationName || "").trim();
    if (!appName && !logoFile && !faviconFile) {
      toast.info("Enter application name or choose a logo/favicon to save");
      return;
    }
    if (!appName) {
      toast.error("Application Name is required");
      return;
    }
    setSavingSection("branding");
    setErrors({});
    const formData = new FormData();
    formData.append("application_name", appName);
    if (logoFile) formData.append("forum_logo", logoFile);
    if (faviconFile) formData.append("forum_favicon", faviconFile);
    putSection(formData)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(FORUM_SETTINGS_UPDATED);
          setLogoFile(null);
          setFaviconFile(null);
          if (typeof refreshForumBranding === "function") {
            refreshForumBranding();
          }
          return axios.get(FORUM_SETTINGS_GET);
        }
      })
      .then((res) => {
        if (res?.status === 200 && res.data.success) {
          const data = res.data.data || {};
          setApplicationName(data.application_name?.content || "");
          setLogoPreview(data.forum_logo?.url || "");
          setFaviconPreview(data.forum_favicon?.url || "");
        }
      })
      .catch(handleApiError)
      .finally(() => setSavingSection(""));
  };

  const saveTerms = () => {
    if (!canEdit) return;
    setSavingSection("terms");
    setErrors({});
    const formData = new FormData();
    formData.append(
      "terms_and_conditions",
      JSON.stringify({ title: terms.title, content: terms.content }),
    );
    putSection(formData)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(FORUM_SETTINGS_UPDATED);
        }
      })
      .catch(handleApiError)
      .finally(() => setSavingSection(""));
  };

  const savePrivacy = () => {
    if (!canEdit) return;
    setSavingSection("privacy");
    setErrors({});
    const formData = new FormData();
    formData.append(
      "privacy_policy",
      JSON.stringify({ title: privacy.title, content: privacy.content }),
    );
    putSection(formData)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(FORUM_SETTINGS_UPDATED);
        }
      })
      .catch(handleApiError)
      .finally(() => setSavingSection(""));
  };

  const saveSmtp = () => {
    if (!canEdit) return;
    setSavingSection("smtp");
    setErrors({});
    const formData = new FormData();
    formData.append("smtp_configuration", JSON.stringify(smtp));
    putSection(formData)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(FORUM_SETTINGS_UPDATED);
          return axios.get(FORUM_SETTINGS_GET);
        }
      })
      .then((res) => {
        if (res?.status === 200 && res.data.success) {
          const cfg = res.data.data?.smtp_configuration?.content || {};
          setSmtp({ ...emptySmtp, ...cfg });
        }
      })
      .catch(handleApiError)
      .finally(() => setSavingSection(""));
  };

  useEffect(() => {
    axios
      .get(FORUM_SETTINGS_GET)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          const data = res.data.data || {};
          setTerms({
            title: data.terms_and_conditions?.title || "Terms and Conditions",
            content: data.terms_and_conditions?.content || "",
          });
          setPrivacy({
            title: data.privacy_policy?.title || "Privacy Policy",
            content: data.privacy_policy?.content || "",
          });
          const cfg = data.smtp_configuration?.content || {};
          setSmtp({ ...emptySmtp, ...cfg });
          setApplicationName(data.application_name?.content || "ZIFF");
          setLogoPreview(data.forum_logo?.url || "");
          setFaviconPreview(data.forum_favicon?.url || "");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <div className="content-wrapper">
      <FullPageLoader visible={loading || !!savingSection} />
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Forum Settings</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">Forum Settings</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow-1">
        <div className="row">
          <div className="col-xl-12">
            {/* Branding */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}>
                  Branding
                </h5>
              </div>
              <div className="card-body app-form-fields">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveBranding();
                  }}
                >
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="application_name">
                          Application Name<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="application_name"
                          placeholder="Application Name"
                          disabled={!canEdit}
                          value={applicationName}
                          onChange={(e) => setApplicationName(e.target.value)}
                        />
                        <small className="text-muted">
                          Shown in welcome email as Your [Application Name] Team
                        </small>
                        <ValidationMessage
                          error={errors}
                          name="application_name"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <label htmlFor="forum_logo" className="form-label">
                            Logo
                          </label>
                          <div className="d-flex gap-3 image-info">
                            <div>
                              <label>Max :</label>
                              <span>2MB</span>
                            </div>
                          </div>
                        </div>
                        <input
                          type="file"
                          className="form-control"
                          id="forum_logo"
                          accept="image/jpeg, image/png, image/jpg, image/webp, image/svg+xml"
                          disabled={!canEdit}
                          onChange={onLogoChange}
                        />
                        {logoPreview ? (
                          <div className="m-3">
                            <Image
                              width={100}
                              src={logoPreview}
                              style={{ objectFit: "contain" }}
                            />
                          </div>
                        ) : null}
                        <ValidationMessage error={errors} name="forum_logo" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <label htmlFor="forum_favicon" className="form-label">
                            Favicon
                          </label>
                          <div className="d-flex gap-3 image-info">
                            <div>
                              <label>Max :</label>
                              <span>2MB</span>
                            </div>
                          </div>
                        </div>
                        <input
                          type="file"
                          className="form-control"
                          id="forum_favicon"
                          accept="image/jpeg, image/png, image/jpg, image/webp, image/x-icon, .ico"
                          disabled={!canEdit}
                          onChange={onFaviconChange}
                        />
                        {faviconPreview ? (
                          <div className="m-3">
                            <Image
                              width={160}
                              height={160}
                              src={faviconPreview}
                              style={{ objectFit: "contain", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "8px", background: "#f8f9fa" }}
                            />
                          </div>
                        ) : null}
                        <ValidationMessage
                          error={errors}
                          name="forum_favicon"
                        />
                      </div>
                    </div>
                    {canEdit && (
                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <button
                          disabled={savingSection === "branding"}
                          type="submit"
                          className="btn btn-success isubmit_button"
                        >
                          {savingSection === "branding"
                            ? "Saving..."
                            : "Save Branding"}
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Terms */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}>
                  Terms and Conditions
                </h5>
              </div>
              <div className="card-body app-form-fields">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveTerms();
                  }}
                >
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Content<span className="text-danger">*</span>
                        </label>
                        <ReactQuill
                          className="forum-legal-editor"
                          theme="snow"
                          readOnly={!canEdit}
                          modules={FORUM_QUILL_MODULES}
                          formats={FORUM_QUILL_FORMATS}
                          value={terms.content}
                          onChange={(content) =>
                            setTerms({ ...terms, content })
                          }
                        />
                        <ValidationMessage error={errors} name="content" />
                      </div>
                    </div>
                    {canEdit && (
                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <button
                          disabled={savingSection === "terms"}
                          type="submit"
                          className="btn btn-success isubmit_button"
                        >
                          {savingSection === "terms"
                            ? "Saving..."
                            : "Save Terms"}
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Privacy */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}>
                  Privacy Policy
                </h5>
              </div>
              <div className="card-body app-form-fields">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    savePrivacy();
                  }}
                >
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Content<span className="text-danger">*</span>
                        </label>
                        <ReactQuill
                          className="forum-legal-editor"
                          theme="snow"
                          readOnly={!canEdit}
                          modules={FORUM_QUILL_MODULES}
                          formats={FORUM_QUILL_FORMATS}
                          value={privacy.content}
                          onChange={(content) =>
                            setPrivacy({ ...privacy, content })
                          }
                        />
                        <ValidationMessage error={errors} name="content" />
                      </div>
                    </div>
                    {canEdit && (
                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <button
                          disabled={savingSection === "privacy"}
                          type="submit"
                          className="btn btn-success isubmit_button"
                        >
                          {savingSection === "privacy"
                            ? "Saving..."
                            : "Save Privacy Policy"}
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* SMTP */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}>
                  SMTP Configuration
                </h5>
              </div>
              <div className="card-body app-form-fields">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveSmtp();
                  }}
                >
                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="mail_from_name">
                          From Name<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="mail_from_name"
                          placeholder="From Name"
                          disabled={!canEdit}
                          value={smtp.mail_from_name}
                          onChange={(e) =>
                            setSmtp({
                              ...smtp,
                              mail_from_name: e.target.value,
                            })
                          }
                        />
                        <ValidationMessage
                          error={errors}
                          name="mail_from_name"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="mail_from_email">
                          From Email<span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="mail_from_email"
                          placeholder="From Email"
                          disabled={!canEdit}
                          value={smtp.mail_from_email}
                          onChange={(e) =>
                            setSmtp({
                              ...smtp,
                              mail_from_email: e.target.value,
                            })
                          }
                        />
                        <ValidationMessage
                          error={errors}
                          name="mail_from_email"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="mail_to_email">
                          To Email<span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="mail_to_email"
                          placeholder="To Email"
                          disabled={!canEdit}
                          value={smtp.mail_to_email}
                          onChange={(e) =>
                            setSmtp({ ...smtp, mail_to_email: e.target.value })
                          }
                        />
                        <ValidationMessage
                          error={errors}
                          name="mail_to_email"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="smtp_host">
                          SMTP Host<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="smtp_host"
                          placeholder="SMTP Host"
                          disabled={!canEdit}
                          value={smtp.smtp_host}
                          onChange={(e) =>
                            setSmtp({ ...smtp, smtp_host: e.target.value })
                          }
                        />
                        <ValidationMessage error={errors} name="smtp_host" />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="smtp_port">
                          SMTP Port<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="smtp_port"
                          placeholder="465"
                          disabled={!canEdit}
                          value={smtp.smtp_port}
                          onChange={(e) =>
                            setSmtp({ ...smtp, smtp_port: e.target.value })
                          }
                        />
                        <ValidationMessage error={errors} name="smtp_port" />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="smtp_user">
                          SMTP Username<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="smtp_user"
                          placeholder="SMTP Username"
                          disabled={!canEdit}
                          value={smtp.smtp_user}
                          onChange={(e) =>
                            setSmtp({ ...smtp, smtp_user: e.target.value })
                          }
                        />
                        <ValidationMessage error={errors} name="smtp_user" />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="smtp_pass">
                          SMTP Password<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="smtp_pass"
                          placeholder="SMTP Password"
                          disabled={!canEdit}
                          value={smtp.smtp_pass}
                          onChange={(e) =>
                            setSmtp({ ...smtp, smtp_pass: e.target.value })
                          }
                          autoComplete="off"
                        />
                        <ValidationMessage error={errors} name="smtp_pass" />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <div className="form-check mt-4">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="smtp_secure"
                            disabled={!canEdit}
                            checked={String(smtp.smtp_secure) === "1"}
                            onChange={(e) =>
                              setSmtp({
                                ...smtp,
                                smtp_secure: e.target.checked ? "1" : "0",
                              })
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="smtp_secure"
                          >
                            Use SSL/TLS (secure)
                          </label>
                        </div>
                        <ValidationMessage error={errors} name="smtp_secure" />
                      </div>
                    </div>
                    {canEdit && (
                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <button
                          disabled={savingSection === "smtp"}
                          type="submit"
                          className="btn btn-success isubmit_button"
                        >
                          {savingSection === "smtp"
                            ? "Saving..."
                            : "Save SMTP Configuration"}
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForumSettingsEdit;
